import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import StopIcon from '@mui/icons-material/Stop';


export const DEFAULT_TEXT_MAX_LENGTH = 200;

type RawModeConfig = {
    id?: string;
    label?: string;
    prompt?: string;
};

type RawCopilotConfig = {
    noteMaxLength?: number;
    speechRecognition?: {
        enabled?: boolean;
        lang?: string;
        stopOnMaxLength?: boolean;
    };
    copilot?: {
        enabled?: boolean;
        modes?: RawModeConfig[];
    };
};

export type CopilotModeConfig = {
    id: string;
    label: string;
    prompt: string;
};

export type AICopilotConfig = {
    noteMaxLength: number;
    speechRecognition: {
        enabled: boolean;
        lang: string;
        stopOnMaxLength: boolean;
    };
    copilot: {
        enabled: boolean;
        modes: CopilotModeConfig[];
    };
};

type SpeechRecognitionAlternativeLike = {
    transcript: string;
};

type SpeechRecognitionResultLike = {
    isFinal: boolean;
    length: number;
    [index: number]: SpeechRecognitionAlternativeLike;
};

type SpeechRecognitionEventLike = Event & {
    resultIndex: number;
    results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionErrorEventLike = Event & {
    error?: string;
};

type SpeechRecognitionLike = {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onstart: (() => void) | null;
    onresult: ((event: SpeechRecognitionEventLike) => void) | null;
    onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
    onend: (() => void) | null;
    start: () => void;
    stop: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

declare global {
    interface Window {
        SpeechRecognition?: SpeechRecognitionCtor;
        webkitSpeechRecognition?: SpeechRecognitionCtor;
        COPILOT_CONFIG_URL?: string;
    }
}


export type AITextAssistRewrite = (input: {
    text: string;
    modeId: string;
    prompt: string;
    maxLength: number;
}) => Promise<{text: string; clipped?: boolean}>;

export type AITextAssistProps = {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    textFieldId?: string;
    label?: string;
    rows?: number;
    maxLength?: number;
    rewriteText?: AITextAssistRewrite;
    placeholder?: string;
};

type NoticeSeverity = 'warning' | 'error' | 'info';

type AssistNotice = {
    severity: NoticeSeverity;
    shortLabel: string;
    message: string;
};

const DEFAULT_CONFIG: AICopilotConfig = {
    noteMaxLength: DEFAULT_TEXT_MAX_LENGTH,
    speechRecognition: {
        enabled: true,
        lang: 'it-IT',
        stopOnMaxLength: true,
    },
    copilot: {
        enabled: false,
        modes: [],
    },
};

let configPromise: Promise<AICopilotConfig> | null = null;

function normalizeMaxLength(value?: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return DEFAULT_TEXT_MAX_LENGTH;
    }
    return Math.floor(parsed);
}

function resolveConfigUrl(): string {
    const custom = String(window.COPILOT_CONFIG_URL || '').trim();
    if (custom) {
        return custom;
    }
    const basePath = process.env.PUBLIC_URL || '';
    return `${basePath}/config/copilot.json`;
}

function normalizeConfig(raw?: RawCopilotConfig | null): AICopilotConfig {
    const noteMaxLength = normalizeMaxLength(raw?.noteMaxLength);
    const maybeModes = raw?.copilot?.modes;
    const rawModes = Array.isArray(maybeModes) ? maybeModes : [];
    const modes = rawModes
        .map((item) => ({
            id: String(item.id || '').trim(),
            label: String(item.label || item.id || '').trim(),
            prompt: String(item.prompt || '').trim(),
        }))
        .filter((item) => item.id && item.label && item.prompt);

    return {
        noteMaxLength,
        speechRecognition: {
            enabled: Boolean(raw?.speechRecognition?.enabled ?? true),
            lang: String(raw?.speechRecognition?.lang || 'it-IT'),
            stopOnMaxLength: Boolean(raw?.speechRecognition?.stopOnMaxLength ?? true),
        },
        copilot: {
            enabled: Boolean(raw?.copilot?.enabled),
            modes,
        },
    };
}

export async function loadAICopilotConfig(): Promise<AICopilotConfig> {
    if (!configPromise) {
        configPromise = fetch(resolveConfigUrl(), {cache: 'no-store'})
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Configurazione Copilot non disponibile');
                }
                return response.json();
            })
            .then((raw) => normalizeConfig(raw as RawCopilotConfig))
            .catch(() => DEFAULT_CONFIG);
    }

    return configPromise;
}

export function clampAIText(value: string, maxLength = DEFAULT_TEXT_MAX_LENGTH): {value: string; clipped: boolean} {
    const normalized = String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (normalized.length <= maxLength) {
        return {value: normalized, clipped: false};
    }
    return {
        value: normalized.slice(0, maxLength),
        clipped: true,
    };
}

function appendDictation(base: string, addition: string): string {
    const left = String(base || '').trimEnd();
    const right = String(addition || '').trim();

    if (!right) {
        return left || base;
    }
    if (!left) {
        return right;
    }
    return left.endsWith('\n') ? `${left}${right}` : `${left} ${right}`;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function humanizeDictationError(errorCode?: string): string {
    const isIframe = typeof window !== 'undefined' && window.self !== window.top;
    switch (errorCode) {
        case 'audio-capture':
            return isIframe
                ? 'Microfono non disponibile. In iframe verifica anche allow="microphone" sul parent.'
                : 'Microfono non disponibile.';
        case 'not-allowed':
        case 'service-not-allowed':
            return isIframe
                ? 'Permesso microfono negato o bloccato dal parent iframe. Verifica allow="microphone" e la Permissions-Policy del sito contenitore.'
                : 'Permesso microfono negato.';
        case 'no-speech':
            return 'Nessun parlato rilevato.';
        case 'network':
            return 'Errore di rete durante la dettatura.';
        case 'aborted':
            return '';
        default:
            return 'Dettatura non disponibile in questo momento.';
    }
}

const AITextAssistant: React.FC<AITextAssistProps> = ({
    value,
    onChange,
    error = false,
    helperText = '',
    disabled = false,
    textFieldId = 'note',
    label = 'Descrivi attività',
    rows = 5,
    maxLength: propsMaxLength,
    rewriteText,
    placeholder,
}) => {
    const [config, setConfig] = useState<AICopilotConfig>(DEFAULT_CONFIG);
    const [copilotAnchorEl, setCopilotAnchorEl] = useState<HTMLElement | null>(null);
    const [copilotLoading, setCopilotLoading] = useState(false);
    const [dictationSupported, setDictationSupported] = useState(false);
    const [dictating, setDictating] = useState(false);
    const [assistNotice, setAssistNotice] = useState<AssistNotice | null>(null);
    const [snackbarNotice, setSnackbarNotice] = useState<AssistNotice | null>(null);
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
    const dictationBaseTextRef = useRef('');
    const dictationFinalTextRef = useRef('');
    const clampedFromRemoteRef = useRef(false);
    const ignoreDictationResultsRef = useRef(false);
    const latestValueRef = useRef(String(value || ''));
    const manualRevisionRef = useRef(0);

    useEffect(() => {
        let active = true;

        loadAICopilotConfig().then((resolved) => {
            if (active) {
                setConfig(resolved);
            }
        });
        setDictationSupported(Boolean(getSpeechRecognitionCtor()));

        return () => {
            active = false;
            recognitionRef.current?.stop();
        };
    }, []);

    useEffect(() => {
        latestValueRef.current = String(value || '');
    }, [value]);

    // const maxLength = config.noteMaxLength || DEFAULT_TEXT_MAX_LENGTH;
    const maxLength = propsMaxLength ?? config.noteMaxLength ?? DEFAULT_TEXT_MAX_LENGTH;
    const canUseCopilot = !disabled && Boolean(rewriteText) && config.copilot.enabled && config.copilot.modes.length > 0;
    const canUseDictation = !disabled && config.speechRecognition.enabled && dictationSupported;

    const setNotice = (nextNotice: AssistNotice | null, openOverlay = false) => {
        setAssistNotice(nextNotice);
        if (nextNotice && openOverlay) {
            setSnackbarNotice(nextNotice);
        }
    };

    useEffect(() => {
        if (String(value || '').length <= maxLength) {
            clampedFromRemoteRef.current = false;
            return;
        }

        if (clampedFromRemoteRef.current) {
            return;
        }

        clampedFromRemoteRef.current = true;
        const next = clampAIText(value, maxLength);
        onChange(next.value);
        setNotice({
            severity: 'warning',
            shortLabel: `>${maxLength}`,
            message: `Il testo caricato superava ${maxLength} caratteri ed è stato ridotto automaticamente.`,
        });
    }, [maxLength, onChange, value]);

    const counterText = useMemo(
        () => `${String(value || '').length}/${maxLength}`,
        [maxLength, value],
    );

    const stopDictation = () => {
        recognitionRef.current?.stop();
    };

    const applyValue = (candidate: string, source: 'manual' | 'dictation' | 'copilot') => {
        const result = clampAIText(candidate, maxLength);
        onChange(result.value);

        if (!result.clipped) {
            return;
        }

        if (source === 'dictation') {
            setNotice({
                severity: 'warning',
                shortLabel: `Max ${maxLength}`,
                message: `Raggiunto il limite di ${maxLength} caratteri. Dettatura interrotta.`,
            });
            if (config.speechRecognition.stopOnMaxLength) {
                stopDictation();
            }
            return;
        }

        if (source === 'copilot') {
            setNotice({
                severity: 'warning',
                shortLabel: 'Taglio AI',
                message: `Il testo generato da Copilot superava ${maxLength} caratteri ed e stato ridotto automaticamente.`,
            });
            return;
        }

        setNotice({
            severity: 'warning',
            shortLabel: `Max ${maxLength}`,
            message: `Il testo e stato ridotto a ${maxLength} caratteri.`,
        });
    };

    const startDictation = () => {
        const RecognitionCtor = getSpeechRecognitionCtor();
        if (!RecognitionCtor || !config.speechRecognition.enabled) {
            setNotice({
                severity: 'error',
                shortLabel: 'Mic KO',
                message: 'La dettatura non e supportata dal browser corrente.',
            }, true);
            return;
        }

        setNotice(null);
        setCopilotAnchorEl(null);
        dictationBaseTextRef.current = String(value || '');
        dictationFinalTextRef.current = '';
        ignoreDictationResultsRef.current = false;

        const recognition = new RecognitionCtor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = config.speechRecognition.lang || 'it-IT';
        recognition.onstart = () => setDictating(true);
        recognition.onresult = (event) => {
            if (ignoreDictationResultsRef.current) {
                return;
            }

            let finalized = dictationFinalTextRef.current;
            let interim = '';

            for (let index = event.resultIndex; index < event.results.length; index += 1) {
                const result = event.results[index];
                const transcript = result?.[0]?.transcript?.trim() || '';
                if (!transcript) {
                    continue;
                }
                if (result.isFinal) {
                    finalized = appendDictation(finalized, transcript);
                } else {
                    interim = appendDictation(interim, transcript);
                }
            }

            dictationFinalTextRef.current = finalized;
            const combined = interim ? appendDictation(finalized, interim) : finalized;
            const nextText = appendDictation(dictationBaseTextRef.current, combined);
            applyValue(nextText, 'dictation');
        };
        recognition.onerror = (event) => {
            const message = humanizeDictationError(event.error);
            if (message) {
                setNotice({
                    severity: 'error',
                    shortLabel: 'Mic KO',
                    message,
                }, true);
            }
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            setDictating(false);

             if (ignoreDictationResultsRef.current) {
                ignoreDictationResultsRef.current = false;
                dictationFinalTextRef.current = '';
                return;
            }

            const finalText = appendDictation(dictationBaseTextRef.current, dictationFinalTextRef.current);
            applyValue(finalText, 'dictation');
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
        } catch {
            recognitionRef.current = null;
            setDictating(false);
            setNotice({
                severity: 'error',
                shortLabel: 'Mic KO',
                message: 'Impossibile avviare la dettatura.',
            }, true);
        }
    };

    const handleCopilotClick = async (modeId: string) => {
        const noteSnapshot = latestValueRef.current;
        const manualRevision = manualRevisionRef.current;

        setCopilotAnchorEl(null);
        setNotice(null);
        setCopilotLoading(true);

        try {
            if (!rewriteText) throw new Error('Servizio Copilot non configurato.');
            const mode = config.copilot.modes.find(item => item.id === modeId);
            if (!mode) throw new Error('Modalita Copilot non configurata.');
            const apiResult = await rewriteText({
                text: noteSnapshot,
                modeId: mode.id,
                prompt: mode.prompt,
                maxLength
            });
            const clamped = clampAIText(apiResult.text, maxLength);
            const result = {
                text: clamped.value,
                clipped: Boolean(apiResult.clipped || clamped.clipped),
                maxLength
            };


            if (manualRevisionRef.current !== manualRevision || latestValueRef.current !== noteSnapshot) {
                setNotice({
                    severity: 'info',
                    shortLabel: 'Bozza mod.',
                    message: 'La nota e stata modificata manualmente durante l elaborazione. Il risultato Copilot e stato scartato.',
                });
                return;
            }

            applyValue(result.text, 'copilot');
            if (result.clipped) {
                setNotice({
                    severity: 'warning',
                    shortLabel: 'Taglio AI',
                    message: `Il testo generato da Copilot superava ${result.maxLength} caratteri ed e stato ridotto automaticamente.`,
                });
            }
        } catch (err: any) {
            const responseMessage = err?.response?.data?.message;
            setNotice({
                severity: 'error',
                shortLabel: 'AI KO',
                message: responseMessage || err?.message || 'Errore durante la chiamata Copilot.',
            }, true);
        } finally {
            setCopilotLoading(false);
        }
    };

    const sideNotice = useMemo<AssistNotice | null>(() => {
        if (error && helperText) {
            return {
                severity: 'error',
                shortLabel: helperText,
                message: helperText,
            };
        }
        if (assistNotice) {
            return assistNotice;
        }
        if (copilotLoading) {
            return {
                severity: 'info',
                shortLabel: 'Copilot...',
                message: 'Copilot in elaborazione.',
            };
        }
        if (dictating) {
            return {
                severity: 'info',
                shortLabel: 'REC',
                message: 'Dettatura attiva.',
            };
        }
        if (!dictationSupported && config.speechRecognition.enabled) {
            return {
                severity: 'warning',
                shortLabel: 'Mic off',
                message: 'La dettatura richiede il supporto SpeechRecognition del browser.',
            };
        }
        return null;
    }, [assistNotice, config.speechRecognition.enabled, copilotLoading, dictating, dictationSupported, error, helperText]);

    const sideNoticeColor = useMemo(() => {
        if (!sideNotice) {
            return 'text.secondary';
        }
        switch (sideNotice.severity) {
            case 'error':
                return 'error.main';
            case 'warning':
                return 'warning.main';
            case 'info':
                return 'primary.main';
            default:
                return 'text.secondary';
        }
    }, [sideNotice]);

    return (
        <Box sx={{width: '100%'}}>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'stretch', width: '100%'}}>
                <TextField
                    id={textFieldId}
                    label={label}
                    fullWidth
                    margin="dense"
                    multiline
                    rows={rows}
                    placeholder={placeholder}
                    value={value || ''}
                    onChange={(event) => {
                        manualRevisionRef.current += 1;
                        setNotice(null);

                        if (dictating) {
                            ignoreDictationResultsRef.current = true;
                            dictationBaseTextRef.current = event.target.value;
                            dictationFinalTextRef.current = '';
                            setNotice({
                                severity: 'info',
                                shortLabel: 'Dett. stop',
                                message: 'Modifica manuale rilevata: la dettatura e stata fermata per mantenere priorita al testo scritto a mano.',
                            });
                            stopDictation();
                        }

                        applyValue(event.target.value, 'manual');
                    }}
                    inputProps={{maxLength}}
                    error={error}
                    disabled={disabled}
                    sx={{flex: 1}}
                />

                <Box
                    sx={{
                        width: 88,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        pt: 0.25,
                    }}
                >
                    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25}}>
                        <Tooltip title="Copilot">
                            <span style={{display: 'block', width: '100%'}}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!canUseCopilot || copilotLoading || dictating || !String(value || '').trim()}
                                    onClick={(event) => setCopilotAnchorEl(event.currentTarget)}
                                    sx={{width: '100%', height: 28, p: 0.4, border: 1, borderColor: 'divider', borderRadius: 2}}
                                >
                                    {copilotLoading ? <CircularProgress size={18} /> : <AutoFixHighIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={dictating ? 'Ferma dettatura' : 'Dettatura'}>
                            <span style={{display: 'block', width: '100%'}}>
                                <IconButton
                                    size="small"
                                    color={dictating ? 'error' : 'default'}
                                    disabled={copilotLoading || (!dictating && !canUseDictation)}
                                    onClick={dictating ? stopDictation : startDictation}
                                    sx={{width: '100%', height: 28, p: 0.4, border: 1, borderColor: dictating ? 'error.main' : 'divider', borderRadius: 2}}
                                >
                                    {dictating ? <StopIcon fontSize="small" /> : <KeyboardVoiceIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <Tooltip title={sideNotice?.message || `${counterText} caratteri`}>
                        <Box
                            sx={{
                                border: 1,
                                borderColor: sideNotice ? sideNoticeColor : 'divider',
                                borderRadius: 2,
                                px: 0.5,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: sideNotice?.shortLabel ? 'space-between' : 'center',
                                gap: 0.5,
                                bgcolor: sideNotice ? 'action.hover' : 'transparent',
                                overflow: 'hidden',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: String(value || '').length >= maxLength ? 'warning.main' : 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {counterText}
                            </Typography>
                            {sideNotice?.shortLabel && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: sideNoticeColor,
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {sideNotice.shortLabel}
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Menu
                anchorEl={copilotAnchorEl}
                open={Boolean(copilotAnchorEl)}
                onClose={() => setCopilotAnchorEl(null)}
            >
                {config.copilot.modes.map((mode) => (
                    <MenuItem key={mode.id} onClick={() => void handleCopilotClick(mode.id)}>
                        {mode.label}
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar
                open={Boolean(snackbarNotice)}
                autoHideDuration={3500}
                onClose={() => setSnackbarNotice(null)}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert severity={snackbarNotice?.severity || 'info'} onClose={() => setSnackbarNotice(null)} variant="filled">
                    {snackbarNotice?.message || ''}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AITextAssistant;
